'use client';

import { createStore, StoreSnapshot } from '@xstate/store';

import { Filter, FilterOption, Filters } from '@/fo/ui/product/layout/catalog';

export interface ContextStoreFilters {
  filters: Filters;
  filterCategory: number;
}

export const initialContext: ContextStoreFilters = {
  filterCategory: 0,
  filters: {
    mainFilters: []
  }
};

export const storeFilters = createStore({
  context: initialContext,
  on: {
    setFilters: (
      context: ContextStoreFilters,
      event: {
        filters: Filters;
        filterCategory: number;
      }
    ) => {
      if (context.filterCategory === event.filterCategory) {
        return {
          ...context,
          filters: context.filters
        };
      }

      context.filterCategory = event.filterCategory;

      return {
        ...context,
        filters: event.filters
      };
    },

    updateFilters: (
      context: ContextStoreFilters,
      event: {
        filters: Filters;
      }
    ) => {
      const newMainFilters = (event.filters.mainFilters ?? []).filter(
        (filter): filter is Filter => typeof filter !== 'undefined'
      );

      const oldFiltersMap = new Map<string, Filter>(
        context.filters.mainFilters
          .filter((filter): filter is Filter => typeof filter !== 'undefined')
          .map((filter) => [filter.slug, filter] as [string, Filter])
      );

      const missingElements = context.filters.mainFilters.filter(
        (filterOld): filterOld is Filter => {
          if (typeof filterOld === 'undefined') return false;
          const existsInNewFilters = newMainFilters.some(
            (filterNew) => filterOld.slug === filterNew.slug
          );
          return !existsInNewFilters;
        }
      );

      let disabledElements: Filter[] = [];

      if (missingElements.length) {
        disabledElements = missingElements.map((item) => ({
          ...item,
          disabled: true
        }));
      }

      const updatedMainFilters = newMainFilters.map((newFilter) => {
        const oldFilter = oldFiltersMap.get(newFilter.slug);

        if (!oldFilter || newFilter.type === 'range') {
          return newFilter;
        }

        const oldOptions = oldFilter.options as FilterOption[];
        const newOptions = newFilter.options as FilterOption[];

        const newOptionsValues = new Set(newOptions.map((opt) => opt.value));

        const missingOptions = oldOptions
          .filter((oldOption) => !newOptionsValues.has(oldOption.value))
          .map((oldOption) => ({
            ...oldOption,
            disabled: true
          }));

        return {
          ...newFilter,
          options: [...newOptions, ...missingOptions]
        };
      });

      return {
        ...context,
        filters: {
          ...event.filters,
          mainFilters: [...updatedMainFilters, ...disabledElements]
        }
      };
    }
  }
});

export const selectFilters = (state: StoreSnapshot<ContextStoreFilters>) =>
  state.context.filters;
